import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Atom, Zap, Globe, Cpu, ChevronRight, Sparkles } from "lucide-react";
import { SiX, SiLinkedin, SiGithub } from "react-icons/si";
import { ROUTE_PATHS, CURRENT_YEAR } from "@/lib/index";
import { Button } from "@/components/ui/button";

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const navLinks = [
    { name: "Centennial", path: ROUTE_PATHS.HOME, icon: Atom },
    { name: "Timeline", path: ROUTE_PATHS.TIMELINE, icon: Zap },
    { name: "Applications", path: ROUTE_PATHS.APPLICATIONS, icon: Cpu },
    { name: "Bahrain Vision", path: ROUTE_PATHS.BAHRAIN, icon: Globe },
    { name: "Quantum Future", path: ROUTE_PATHS.FUTURE, icon: Sparkles },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground selection:bg-primary/20 selection:text-primary">
      {/* Header */}
      <header
        className={`fixed top-0 w-full z-50 transition-all duration-300 border-b ${
          scrolled
            ? "bg-background/80 backdrop-blur-md border-border py-3 shadow-sm"
            : "bg-transparent border-transparent py-5"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Link
            to={ROUTE_PATHS.HOME}
            className="flex items-center gap-2 group transition-transform hover:scale-[1.02]"
          >
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center text-primary-foreground shadow-lg shadow-primary/20">
              <Atom className="w-6 h-6 animate-pulse" />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-xl tracking-tight leading-none">
                QUANTUM<span className="text-accent">100</span>
              </span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
                Bahrain Initiative
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-lg text-sm font-medium transition-all hover:bg-muted ${
                    isActive
                      ? "text-primary bg-primary/5"
                      : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {link.name}
              </NavLink>
            ))}
            <div className="ml-4 border-l border-border pl-4">
              <Button variant="default" size="sm" className="rounded-full px-6 bg-accent hover:bg-accent/90 text-accent-foreground">
                Connect
              </Button>
            </div>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2 rounded-lg text-muted-foreground hover:bg-muted"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-background border-b border-border overflow-hidden"
            >
              <div className="px-4 pt-2 pb-6 space-y-1">
                {navLinks.map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-4 rounded-xl text-base font-medium ${
                        isActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground"
                      }`
                    }
                  >
                    <link.icon className="w-5 h-5" />
                    {link.name}
                  </NavLink>
                ))}
                <div className="pt-4 px-4">
                  <Button className="w-full rounded-xl py-6">Get Involved</Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-grow pt-24">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground">
                  <Atom className="w-5 h-5" />
                </div>
                <span className="font-bold text-xl tracking-tight">
                  QUANTUM<span className="text-accent">CENTENNIAL</span>
                </span>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
                Celebrating 100 years of quantum mechanics. From Heisenberg's matrices to Bahrain's national quantum strategy, we explore the particles that define our reality.
              </p>
              <div className="flex gap-4">
                <a href="#" className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors">
                  <SiX className="w-4 h-4" />
                </a>
                <a href="#" className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors">
                  <SiLinkedin className="w-4 h-4" />
                </a>
                <a href="#" className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors">
                  <SiGithub className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-6 text-foreground">Navigation</h4>
              <ul className="space-y-4">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center group"
                    >
                      <ChevronRight className="w-3 h-3 mr-2 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-6 text-foreground">Strategic Pillars</h4>
              <ul className="space-y-4">
                <li>
                  <span className="text-sm text-muted-foreground cursor-default hover:text-accent transition-colors">
                    Drug Discovery & Biotech
                  </span>
                </li>
                <li>
                  <span className="text-sm text-muted-foreground cursor-default hover:text-accent transition-colors">
                    Cyber Security & NCSC
                  </span>
                </li>
                <li>
                  <span className="text-sm text-muted-foreground cursor-default hover:text-accent transition-colors">
                    Financial Cryptography
                  </span>
                </li>
                <li>
                  <span className="text-sm text-muted-foreground cursor-default hover:text-accent transition-colors">
                    Regional Quantum Hub
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-6 text-foreground">Newsletter</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Stay updated on the second quantum revolution in the GCC region.
              </p>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="bg-muted border border-border rounded-lg px-4 py-2 text-sm flex-grow focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <Button size="sm" className="shrink-0">Join</Button>
              </div>
            </div>
          </div>

          <div className="border-t border-border mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-muted-foreground font-mono">
              &copy; {CURRENT_YEAR} QUANTUM CENTENNIAL BAHRAIN. ALL RIGHTS OBSERVED.
            </p>
            <div className="flex gap-8 text-xs text-muted-foreground">
              <a href="#" className="hover:text-foreground transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-foreground transition-colors">Terms of Research</a>
              <a href="#" className="hover:text-foreground transition-colors">Institutional Access</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
